import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, LoginRequest, SignupRequest, UserProfile } from '@/services/api'
import toast from 'react-hot-toast'

interface AuthState {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  
  // Actions
  login: (data: LoginRequest) => Promise<void>
  signup: (data: SignupRequest) => Promise<void>
  logout: () => Promise<void>
  initializeAuth: () => Promise<void>
  updateUser: (user: Partial<UserProfile>) => void
  clearAuth: () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,

      login: async (data: LoginRequest) => {
        try {
          set({ isLoading: true })
          
          const response = await api.login(data)
          
          // In a real implementation, the backend should return user profile
          // For now, we'll create a minimal user object
          const user: UserProfile = {
            userId: response.user.userId,
            email: response.user.email,
            isActive: true,
            isVerified: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            securityStatus: {
              hasActiveApiKeys: false,
              accountLocked: false,
              failedLoginAttempts: 0,
              emailVerified: false,
              recentActivity: false,
            },
            statistics: {
              totalApiKeys: 0,
              activeApiKeys: 0,
              recentApiKeys: 0,
              usage: {
                totalRequests: 0,
                uniqueEndpoints: 0,
                uniqueModels: 0,
                totalTokens: 0,
                totalCost: 0,
                avgRequestDuration: 0,
              },
            },
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })

          // Store user in localStorage for persistence
          api.setUserInStorage(user)

          toast.success('Welcome back! Login successful.')
        } catch (error: any) {
          set({ isLoading: false })
          toast.error(error.message || 'Login failed')
          throw error
        }
      },

      signup: async (data: SignupRequest) => {
        try {
          set({ isLoading: true })
          
          const response = await api.signup(data)
          
          toast.success(response.message || 'Account created successfully!')
          
          // Don't set user as authenticated since email verification might be required
          set({ isLoading: false })
        } catch (error: any) {
          set({ isLoading: false })
          toast.error(error.message || 'Signup failed')
          throw error
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true })
          
          await api.logout()
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
          
          toast.success('Logged out successfully')
        } catch (error: any) {
          console.error('Logout error:', error)
          // Still clear auth even if API call fails
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
          toast.success('Logged out successfully')
        }
      },

      initializeAuth: async () => {
        try {
          set({ isLoading: true })
          
          // Check if we have a stored auth token
          if (api.isAuthenticated()) {
            const storedUser = api.getUserFromStorage()
            
            if (storedUser) {
              // Try to fetch fresh user data from API
              try {
                const freshUser = await api.getUserProfile()
                set({
                  user: freshUser,
                  isAuthenticated: true,
                  isInitialized: true,
                  isLoading: false,
                })
                api.setUserInStorage(freshUser)
              } catch (error) {
                // If API call fails, use stored user data but mark as potentially stale
                console.warn('Failed to fetch fresh user data, using stored data')
                set({
                  user: storedUser,
                  isAuthenticated: true,
                  isInitialized: true,
                  isLoading: false,
                })
              }
            } else {
              // Token exists but no user data - clear auth
              api.clearAuth()
              set({
                user: null,
                isAuthenticated: false,
                isInitialized: true,
                isLoading: false,
              })
            }
          } else {
            // No token found
            set({
              user: null,
              isAuthenticated: false,
              isInitialized: true,
              isLoading: false,
            })
          }
        } catch (error: any) {
          console.error('Auth initialization error:', error)
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true,
            isLoading: false,
          })
        }
      },

      updateUser: (userData: Partial<UserProfile>) => {
        const currentUser = get().user
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData }
          set({ user: updatedUser })
          api.setUserInStorage(updatedUser)
        }
      },

      clearAuth: () => {
        api.clearAuth()
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)